import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '@/services/api';
import { Plus, Edit, Trash2, X, Briefcase } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import '@/styles/AdminManagement.css';

const AdminExperience = () => {
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingExp, setEditingExp] = useState(null);
  const [formData, setFormData] = useState({
    company: '', position: '', duration: '', current: false,
    achievements: [''], technologies: []
  });
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (!adminApi.isAuthenticated()) { navigate('/admin/login'); return; }
    loadExperiences();
  }, [navigate]);

  const loadExperiences = async () => {
    try {
      const data = await adminApi.getAllExperiences();
      setExperiences(data);
    } catch (error) {
      console.error('Failed to load experiences:', error);
    } finally {
      setLoading(false);
    }
  };

  const openForm = (exp = null) => {
    if (exp) {
      setEditingExp(exp);
      setFormData({
        company: exp.company,
        position: exp.position,
        duration: exp.duration,
        current: exp.current || false,
        achievements: exp.achievements || [''],
        technologies: exp.technologies || []
      });
    } else {
      setEditingExp(null);
      setFormData({
        company: '', position: '', duration: '', current: false,
        achievements: [''], technologies: []
      });
    }
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const expData = {
        ...formData,
        achievements: formData.achievements.filter(a => a.trim() !== ''),
        technologies: formData.technologies.filter(t => t.trim() !== '')
      };

      if (editingExp) {
        await adminApi.updateExperience(editingExp.id, expData);
        setExperiences(experiences.map(ex => ex.id === editingExp.id ? { ...editingExp, ...expData } : ex));
        toast({ title: "Experience updated!" });
      } else {
        const newExp = await adminApi.createExperience(expData);
        setExperiences([newExp, ...experiences]);
        toast({ title: "Experience created!" });
      }
      setShowForm(false);
    } catch (error) {
      toast({ title: "Failed to save experience", variant: "destructive" });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this experience?')) return;
    try {
      await adminApi.deleteExperience(id);
      setExperiences(experiences.filter(ex => ex.id !== id));
      toast({ title: "Experience deleted" });
    } catch (error) {
      toast({ title: "Failed to delete", variant: "destructive" });
    }
  };

  const addAchievement = () => {
    setFormData({ ...formData, achievements: [...formData.achievements, ''] });
  };

  const updateAchievement = (index, value) => {
    const updated = [...formData.achievements];
    updated[index] = value;
    setFormData({ ...formData, achievements: updated });
  };

  const removeAchievement = (index) => {
    const updated = formData.achievements.filter((_, i) => i !== index);
    setFormData({ ...formData, achievements: updated.length ? updated : [''] });
  };

  if (loading) return <div className="admin-loading">Loading...</div>;

  return (
    <div className="admin-dashboard">
      <aside className="admin-sidebar">
        <div className="sidebar-header"><h2>AK Admin</h2></div>
        <nav className="sidebar-nav">
          <button className="nav-item" onClick={() => navigate('/admin/dashboard')}>Dashboard</button>
          <button className="nav-item" onClick={() => navigate('/admin/contacts')}>Messages</button>
          <button className="nav-item" onClick={() => navigate('/admin/projects')}>Projects</button>
          <button className="nav-item" onClick={() => navigate('/admin/articles')}>Articles</button>
          <button className="nav-item active">Experience</button>
          <button className="nav-item" onClick={() => navigate('/admin/settings')}>Settings</button>
        </nav>
      </aside>

      <main className="admin-content">
        <div className="content-header">
          <h1>Manage Experience ({experiences.length})</h1>
          <button className="add-btn" onClick={() => openForm()} data-testid="add-experience-btn">
            <Plus size={20} />Add Experience
          </button>
        </div>

        <div className="experience-list">
          {experiences.map((exp) => (
            <div key={exp.id} className="experience-card" data-testid={`experience-card-${exp.id}`}>
              <div className="exp-header">
                <div className="exp-icon">
                  <Briefcase size={24} />
                </div>
                <div className="exp-info">
                  <h3>{exp.position}</h3>
                  <div className="exp-company">{exp.company}</div>
                  <div className="exp-duration">
                    {exp.duration}
                    {exp.current && <span className="current-badge">Current</span>}
                  </div>
                </div>
              </div>
              
              {exp.achievements && exp.achievements.length > 0 && (
                <div className="exp-achievements">
                  <h4>Achievements</h4>
                  <ul>
                    {exp.achievements.map((ach, idx) => (
                      <li key={idx}>{ach}</li>
                    ))}
                  </ul>
                </div>
              )}

              {exp.technologies && exp.technologies.length > 0 && (
                <div className="exp-tech">
                  {exp.technologies.map((tech, idx) => (
                    <span key={idx} className="tech-badge">{tech}</span>
                  ))}
                </div>
              )}

              <div className="item-actions">
                <button onClick={() => openForm(exp)} className="action-btn" data-testid={`edit-exp-${exp.id}`}>
                  <Edit size={16} />Edit
                </button>
                <button onClick={() => handleDelete(exp.id)} className="action-btn danger" data-testid={`delete-exp-${exp.id}`}>
                  <Trash2 size={16} />Delete
                </button>
              </div>
            </div>
          ))}
        </div>

        {showForm && (
          <div className="modal-overlay" onClick={() => setShowForm(false)}>
            <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingExp ? 'Edit Experience' : 'Add Experience'}</h2>
                <button onClick={() => setShowForm(false)} className="close-btn"><X size={24} /></button>
              </div>
              <form onSubmit={handleSubmit} className="modal-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Position/Role</label>
                    <input required value={formData.position} onChange={(e) => setFormData({...formData, position: e.target.value})} placeholder="Senior Flutter Developer" />
                  </div>
                  <div className="form-group">
                    <label>Company</label>
                    <input required value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} placeholder="Layer" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Duration</label>
                    <input required value={formData.duration} onChange={(e) => setFormData({...formData, duration: e.target.value})} placeholder="May 2025 - Present" />
                  </div>
                  <div className="form-group checkbox-group">
                    <label>
                      <input type="checkbox" checked={formData.current} onChange={(e) => setFormData({...formData, current: e.target.checked})} />
                      Currently working here
                    </label>
                  </div>
                </div>
                <div className="form-group">
                  <label>Technologies (comma separated)</label>
                  <input value={formData.technologies.join(', ')} onChange={(e) => setFormData({...formData, technologies: e.target.value.split(',').map(t => t.trim())})} placeholder="Flutter, Dart, Firebase" />
                </div>
                <div className="form-group">
                  <label>Achievements</label>
                  {formData.achievements.map((ach, index) => (
                    <div key={index} className="achievement-input">
                      <input value={ach} onChange={(e) => updateAchievement(index, e.target.value)} placeholder="Describe an achievement..." />
                      {formData.achievements.length > 1 && (
                        <button type="button" onClick={() => removeAchievement(index)} className="remove-btn">
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={addAchievement} className="add-achievement-btn">
                    <Plus size={16} /> Add Achievement
                  </button>
                </div>
                <button type="submit" className="submit-btn">{editingExp ? 'Update' : 'Create'} Experience</button>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminExperience;
